<template>
  <div class="room-renovation-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('roomRenovationManage.page.title')" :subtitle="$t('roomRenovationManage.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="openAddRoomRenovationModal">
          {{ $t('roomRenovationManage.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="roomRenovationManageInfo.conditions.roomName" clearable class="w-full"
            :placeholder="$t('roomRenovationManage.roomNumberPlaceholder')"
            @keyup.enter.native="queryRoomRenovationMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="roomRenovationManageInfo.conditions.personName" clearable class="w-full"
            :placeholder="$t('roomRenovationManage.contactPersonPlaceholder')"
            @keyup.enter.native="queryRoomRenovationMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="roomRenovationManageInfo.conditions.personTel" clearable class="w-full"
            :placeholder="$t('roomRenovationManage.phonePlaceholder')" maxlength="11"
            @keyup.enter.native="queryRoomRenovationMethod" />
        </el-col>
      </el-row>

      <!-- 更多查询条件 -->
      <div v-show="roomRenovationManageInfo.moreCondition">
        <el-row :gutter="16" class="ds-more-row">
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-select v-model="roomRenovationManageInfo.conditions.state" clearable class="w-full"
              :placeholder="$t('roomRenovationManage.statusPlaceholder')">
              <el-option v-for="(item, index) in roomRenovationManageInfo.states" :key="index" :label="item.name"
                :value="item.statusCd" />
            </el-select>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-select v-model="roomRenovationManageInfo.conditions.isPostpone" clearable class="w-full"
              :placeholder="$t('roomRenovationManage.postponePlaceholder')">
              <el-option :label="$t('roomRenovationManage.yes')" value="Y" />
              <el-option :label="$t('roomRenovationManage.no')" value="N" />
            </el-select>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-date-picker v-model="roomRenovationManageInfo.conditions.renovationTime" type="date" class="w-full"
              :placeholder="$t('roomRenovationManage.renovationTimePlaceholder')" value-format="yyyy-MM-dd" />
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-date-picker v-model="roomRenovationManageInfo.conditions.startTime" type="date" class="w-full"
              :placeholder="$t('roomRenovationManage.startTimePlaceholder')" value-format="yyyy-MM-dd" />
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-date-picker v-model="roomRenovationManageInfo.conditions.endTime" type="date" class="w-full"
              :placeholder="$t('roomRenovationManage.endTimePlaceholder')" value-format="yyyy-MM-dd" />
          </el-col>
        </el-row>
      </div>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="queryRoomRenovationMethod">
          {{ $t('roomRenovationManage.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetRoomRenovationMethod">
          {{ $t('roomRenovationManage.reset') }}
        </el-button>
        <el-button type="text" icon="el-icon-more" @click="toggleMoreCondition">
          {{ roomRenovationManageInfo.moreCondition ? $t('roomRenovationManage.hide') : $t('roomRenovationManage.more') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="refreshList">
        <template #left>
          <span class="ds-panel__title">{{ $t('roomRenovationManage.renovationInfo') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="roomRenovationManageInfo.roomRenovations" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border
        :empty-description="$t('roomRenovationManage.page.empty')" @retry="refreshList">
        <el-table-column prop="roomName" :label="$t('roomRenovationManage.room')" align="center" min-width="120" />
        <el-table-column prop="personName" :label="$t('roomRenovationManage.contactPerson')" align="center" width="100" />
        <el-table-column prop="personTel" :label="$t('roomRenovationManage.phone')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.personTel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('roomRenovationManage.renovationTime')" align="center" width="150">
          <template slot-scope="scope">
            <div class="ds-num">{{ scope.row.startTime || '-' }}</div>
            <div class="ds-num">{{ scope.row.endTime || '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('roomRenovationManage.applyTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="renovationCompany" :label="$t('roomRenovationManage.renovationCompany')" align="center"
          min-width="120" show-overflow-tooltip />
        <el-table-column prop="personMain" :label="$t('roomRenovationManage.renovationManager')" align="center" width="100" />
        <el-table-column prop="personMainTel" :label="$t('roomRenovationManage.managerPhone')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.personMainTel || '-' }}</span>
          </template>
        </el-table-column>
        <!-- 状态 Tag：颜色仅作辅助，始终带文字（WCAG 1.4.1） -->
        <el-table-column prop="stateName" :label="$t('roomRenovationManage.status')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('roomRenovationManage.isPostponed')" align="center" width="90">
          <template slot-scope="scope">
            {{ scope.row.isPostpone === 'Y' ? $t('roomRenovationManage.yes') : $t('roomRenovationManage.no') }}
          </template>
        </el-table-column>
        <el-table-column prop="postponeTime" :label="$t('roomRenovationManage.postponeTime')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.postponeTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="isViolation" :label="$t('roomRenovationManage.isViolation')" align="center" width="90">
          <template slot-scope="scope">
            {{ scope.row.isViolation === 'Y' ? $t('roomRenovationManage.yes') : $t('roomRenovationManage.no') }}
          </template>
        </el-table-column>
        <el-table-column prop="violationDesc" :label="$t('roomRenovationManage.violationDesc')" align="left" min-width="140"
          show-overflow-tooltip />
        <el-table-column prop="remark" :label="$t('roomRenovationManage.remark')" align="left" min-width="140"
          show-overflow-tooltip />
        <el-table-column :label="$t('roomRenovationManage.operation')" align="center" width="180" fixed="right">
          <template slot-scope="scope">
            <!-- 操作列三档化：主 1 / 次 2 / 更多 ▾ -->
            <el-button v-if="scope.row.state === '1000' && scope.row.isViolation === 'N'" size="mini" type="text"
              @click="openToExamine(scope.row)">
              {{ $t('roomRenovationManage.review') }}
            </el-button>
            <el-button v-if="scope.row.state === '3000' && scope.row.isViolation === 'N'" size="mini" type="text"
              @click="openDecorationCompleted(scope.row)">
              {{ $t('roomRenovationManage.completeRenovation') }}
            </el-button>
            <el-button v-if="scope.row.state === '4000' && scope.row.isViolation === 'N'" size="mini" type="text"
              @click="openDecorationAcceptanceModel(scope.row)">
              {{ $t('roomRenovationManage.renovationAcceptance') }}
            </el-button>
            <el-button v-if="scope.row.state === '5000' || scope.row.state === '6000'" size="mini" type="text"
              @click="openRoomRenovationDetail(scope.row)">
              {{ $t('roomRenovationManage.acceptanceDetail') }}
            </el-button>

            <el-dropdown trigger="click" @command="cmd => handleMoreCommand(cmd, scope.row)">
              <el-button size="mini" type="text" class="ds-more-btn">
                {{ $t('common.more') }}<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="edit">{{ $t('roomRenovationManage.modify') }}</el-dropdown-item>
                <el-dropdown-item command="record">{{ $t('roomRenovationManage.trackRecord') }}</el-dropdown-item>
                <el-dropdown-item command="delete" divided class="ds-dropdown-danger">
                  {{ $t('roomRenovationManage.delete') }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('roomRenovationManage.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 子组件 -->
    <add-room-renovation ref="addRoomRenovation" @success="listRoomRenovation" />
    <room-decoration-acceptance ref="roomDecorationAcceptance" @success="listRoomRenovation" />
    <room-to-examine ref="roomToExamine" @success="listRoomRenovation" />
    <edit-room-renovation ref="editRoomRenovation" @success="listRoomRenovation" />
    <delete-room-renovation ref="deleteRoomRenovation" @success="listRoomRenovation" />
    <room-renovation-completed ref="roomRenovationCompleted" @success="listRoomRenovation" />
  </div>
</template>

<script>
import { listRoomRenovation } from '@/api/community/roomRenovationManageApi'
import AddRoomRenovation from '@/components/community/AddRoomRenovation'
import RoomDecorationAcceptance from '@/components/community/RoomDecorationAcceptance'
import RoomToExamine from '@/components/community/RoomToExamine'
import EditRoomRenovation from '@/components/community/EditRoomRenovation'
import DeleteRoomRenovation from '@/components/community/DeleteRoomRenovation'
import RoomRenovationCompleted from '@/components/community/RoomRenovationCompleted'
import { getDict } from '@/api/community/communityApi'
import { formatDateTimeSec } from '@/utils/format'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'RoomRenovationManageList',
  components: {
    AddRoomRenovation,
    RoomDecorationAcceptance,
    RoomToExamine,
    EditRoomRenovation,
    DeleteRoomRenovation,
    RoomRenovationCompleted,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      roomRenovationManageInfo: {
        roomRenovations: [],
        moreCondition: false,
        states: [],
        conditions: {
          roomName: '',
          personName: '',
          personTel: '',
          state: '',
          isPostpone: '',
          renovationTime: '',
          startTime: '',
          endTime: '',
          communityId: ''
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getDictData()
    this.listRoomRenovation()
  },
  methods: {
    // 获取字典数据
    async getDictData() {
      try {
        const res = await getDict('room_renovation', 'state')
        this.roomRenovationManageInfo.states = res || []
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },

    // 查询装修信息
    async listRoomRenovation() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.roomRenovationManageInfo.conditions,
          page: this.page.current,
          row: this.page.size,
          communityId: this.getCommunityId()
        }

        const { data, total } = await listRoomRenovation(params)
        this.roomRenovationManageInfo.roomRenovations = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('查询装修信息失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('roomRenovationManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },

    /**
     * 刷新当前页（工具条刷新 / 错误态重试）
     */
    refreshList() {
      this.listRoomRenovation()
    },

    // 切换更多条件
    toggleMoreCondition() {
      this.roomRenovationManageInfo.moreCondition = !this.roomRenovationManageInfo.moreCondition
    },

    // 查询
    queryRoomRenovationMethod() {
      this.page.current = 1
      this.listRoomRenovation()
    },

    // 重置
    resetRoomRenovationMethod() {
      this.roomRenovationManageInfo.conditions = {
        roomName: '',
        personName: '',
        personTel: '',
        state: '',
        isPostpone: '',
        renovationTime: '',
        startTime: '',
        endTime: '',
        communityId: this.getCommunityId()
      }
      this.page.current = 1
      this.listRoomRenovation()
    },

    // 分页大小改变
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.listRoomRenovation()
    },

    // 当前页改变
    handleCurrentChange(val) {
      this.page.current = val
      this.listRoomRenovation()
    },

    // 打开添加模态框
    openAddRoomRenovationModal() {
      this.$refs.addRoomRenovation.open()
    },

    // 打开修改模态框
    openEditRoomRenovationModel(row) {
      this.$refs.editRoomRenovation.open(row)
    },

    // 打开删除模态框
    openDeleteRoomRenovationModel(row) {
      this.$refs.deleteRoomRenovation.open(row)
    },

    // 打开审核模态框
    openToExamine(row) {
      this.$refs.roomToExamine.open(row)
    },

    // 打开验收模态框
    openDecorationAcceptanceModel(row) {
      this.$refs.roomDecorationAcceptance.open(row)
    },

    // 打开装修完成模态框
    openDecorationCompleted(row) {
      this.$refs.roomRenovationCompleted.open(row)
    },

    // 打开费用页面
    openRoomRenovationFee(row) {
      this.$router.push({
        path: '/fee/room-fee',
        query: { roomId: row.roomId }
      })
    },

    // 打开跟踪记录
    openRoomDecorationRecord(row) {
      this.$router.push({
        path: '/views/community/listRoomDecorationRecord',
        query: {
          roomId: row.roomId,
          rId: row.rId,
          roomName: row.roomName,
          state: row.state,
          stateName: row.stateName
        }
      })
    },

    // 打开验收明细
    openRoomRenovationDetail(row) {
      this.$router.push({
        path: '/views/community/listRoomDecorationRecord',
        query: {
          roomId: row.roomId,
          rId: row.rId,
          roomName: row.roomName,
          state: row.state,
          stateName: row.stateName
        }
      })
    },

    /**
     * 操作列「更多」下拉命令分发（保持与改造前完全一致的行为）
     */
    handleMoreCommand(command, row) {
      if (command === 'edit') {
        this.openEditRoomRenovationModel(row)
      } else if (command === 'record') {
        this.openRoomDecorationRecord(row)
      } else if (command === 'delete') {
        this.openDeleteRoomRenovationModel(row)
      }
    },

    /**
     * 申请时间格式化（后端返回 "yyyy-MM-dd HH:mm:ss" 字符串）
     */
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },

    /**
     * 状态语义类型（房屋装修）：
     * 1000 待审核 / 2000 审核不通过 / 3000 装修中 / 4000 待验收 / 5000 验收成功 / 6000 验收失败
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'roomRenovation',
        text: row.stateName || ''
      }).type
    }
  }
}
</script>

<style lang="scss" scoped>
.room-renovation-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 更多条件行间距 */
.ds-more-row {
  margin-top: var(--ds-spacing-md);
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 操作列「更多」下拉 */
.ds-more-btn {
  font-weight: 500;
}

::v-deep .ds-dropdown-danger {
  color: var(--ds-danger) !important;
}
</style>
